import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  IconHome,
  IconUserSearch,
  IconReportAnalytics,
  IconAnalyze,
  IconBooks,
  IconAdjustments,
  IconSettings,
  IconLogout,
  IconMenu2,
  IconCircleX,
} from '@tabler/icons-react';

/* ----------------------------------------------------------
   Shared Icon wrapper
   Default size: 32px, stroke: 1.5px
---------------------------------------------------------- */
const Icon = ({ icon: IconComponent, size = 32, stroke = 1.5, ...props }) => (
  <IconComponent size={size} strokeWidth={stroke} {...props} />
);

/* ----------------------------------------------------------
   Nav item definitions
---------------------------------------------------------- */
const NAV_ITEMS = [
  {
    label: 'Home',
    path: '/dashboard',
    icon: IconHome,
    roles: ['provider', 'clinical_staff', 'non_clinical_staff', 'admin'],
  },
  {
    label: 'Patients',
    path: '/patients',
    icon: IconUserSearch,
    roles: ['provider', 'clinical_staff', 'admin'],
  },
  {
    label: 'Reports',
    path: '/reports',
    icon: IconReportAnalytics,
    roles: ['provider', 'non_clinical_staff', 'admin'],
  },
  {
    label: 'Referral Hub',
    path: '/referrals',
    icon: IconAnalyze,
    roles: ['provider', 'clinical_staff', 'admin'],
  },
  {
    label: 'Resources',
    path: '/resources',
    icon: IconBooks,
    roles: ['provider', 'clinical_staff', 'non_clinical_staff', 'admin'],
  },
];

const NAV_WIDTH_COLLAPSED = '60px';
const NAV_WIDTH_EXPANDED  = '176px';

/* ----------------------------------------------------------
   NavPane component
---------------------------------------------------------- */
export default function NavPane({ expanded, setExpanded }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname.startsWith(path);
  const userRole = user?.role || '';
  const visibleItems = NAV_ITEMS.filter(item => item.roles.includes(userRole));
  const isAdmin = userRole === 'admin';

  const getInitials = () => {
    const first = user?.first_name?.[0] || '';
    const last = user?.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const navWidth = expanded ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED;

  return (
    <div style={{ ...styles.container, width: navWidth, minWidth: navWidth }}>

      {/* Logo area */}
      <div style={{
        ...styles.logoArea,
        justifyContent: expanded ? 'flex-start' : 'center',
        padding: expanded ? '10px 14px' : '10px 0',
      }}>
        <div style={styles.logoPlaceholder}>N</div>
        {expanded && (
          <span style={styles.wordmark}>Nephros</span>
        )}
      </div>

      {/* Toggle button — below logo */}
      <button
        onClick={() => setExpanded(prev => !prev)}
        style={{
          ...styles.rowButton,
          justifyContent: expanded ? 'flex-start' : 'center',
          padding: expanded ? '8px 14px' : '8px 0',
        }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
        title={expanded ? 'Close' : 'Expand'}
      >
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <Icon
            icon={expanded ? IconCircleX : IconMenu2}
            size={32}
            stroke={1.5}
            color="rgba(255,255,255,0.85)"
          />
        </div>
        {expanded && (
          <span style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: '500',
            lineHeight: 'var(--line-height-tight)',
            whiteSpace: 'nowrap',
            fontFamily: 'var(--font-family-base)',
            color: 'rgba(255,255,255,0.85)',
          }}>
            Close
          </span>
        )}
      </button>

      <div style={styles.divider} />

      {/* Main navigation */}
      <nav style={styles.nav}>
        {visibleItems.map((item) => {
          const active = isActive(item.path);
          return (
            <NavButton
              key={item.path}
              label={item.label}
              icon={item.icon}
              active={active}
              expanded={expanded}
              onClick={() => navigate(item.path)}
            />
          );
        })}
      </nav>

      {/* Bottom cluster */}
      <div style={styles.bottomCluster}>
        <div style={styles.divider} />

        {/* Site Settings — admin only */}
        {isAdmin && (
          <NavButton
            label="Site Settings"
            icon={IconAdjustments}
            active={isActive('/settings')}
            expanded={expanded}
            onClick={() => navigate('/settings')}
          />
        )}

        {/* My Account — all users */}
        <NavButton
          label="My Account"
          icon={IconSettings}
          active={isActive('/account')}
          expanded={expanded}
          onClick={() => navigate('/account')}
        />

        <div style={styles.divider} />

        {/* User block — initials + name */}
        <div style={{
          ...styles.userBlock,
          justifyContent: expanded ? 'flex-start' : 'center',
          padding: expanded ? '10px 14px' : '10px 0',
        }}>
          <div
            style={styles.initialsCircle}
            title={`${user?.first_name} ${user?.last_name}`}
          >
            {getInitials()}
          </div>
          {expanded && (
            <span style={styles.userName}>
              {user?.first_name} {user?.last_name}
            </span>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={logout}
          style={{
            ...styles.rowButton,
            justifyContent: expanded ? 'flex-start' : 'center',
            padding: expanded ? '10px 14px' : '10px 0',
            marginBottom: 'var(--space-4)',
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          aria-label="Log out"
          title="Log out"
        >
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <Icon icon={IconLogout} color="rgba(255,255,255,0.85)" />
          </div>
          {expanded && (
            <span style={{
              fontSize: 'var(--font-size-sm)',
             fontWeight: '500',
              lineHeight: 'var(--line-height-tight)',
             whiteSpace: 'nowrap',
             fontFamily: 'var(--font-family-base)',
              color: 'rgba(255,255,255,0.85)',
            }}>
              Log out
            </span>
          )}
        </button>

      </div>
    </div>
  );
}

/* ----------------------------------------------------------
   NavButton sub-component
---------------------------------------------------------- */
function NavButton({ label, icon, active, expanded, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      style={{
        ...styles.navItem,
        justifyContent: expanded ? 'flex-start' : 'center',
        padding: expanded ? '10px 14px' : '10px 0',
        backgroundColor: active
          ? 'rgba(255,255,255,0.18)'
          : hovered
            ? 'rgba(255,255,255,0.12)'
            : 'transparent',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={label}
      title={!expanded ? label : undefined}
    >
      {active && <span style={styles.activeIndicator} />}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <Icon
          icon={icon}
          color={active ? '#ffffff' : 'rgba(255,255,255,0.85)'}
        />
      </div>
      {expanded && (
        <span style={{
          ...styles.navLabel,
          color: active ? '#ffffff' : 'rgba(255,255,255,0.85)',
          fontWeight: active ? '600' : '500',
        }}>
          {label}
        </span>
      )}
    </button>
  );
}

/* ----------------------------------------------------------
   Styles
---------------------------------------------------------- */
const styles = {
  container: {
    height: '100vh',
    backgroundColor: 'var(--color-nav)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 100,
    overflowY: 'auto',
    overflowX: 'hidden',
    transition: 'width 0.2s ease, min-width 0.2s ease',
  },
  logoArea: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0,
  },
  logoPlaceholder: {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-bold)',
    color: '#ffffff',
    letterSpacing: '-0.5px',
    flexShrink: 0,
  },
  wordmark: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-bold)',
    color: '#ffffff',
    letterSpacing: '-0.3px',
    whiteSpace: 'nowrap',
    fontFamily: 'var(--font-family-base)',
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(255,255,255,0.12)',
    margin: '4px 12px',
    flexShrink: 0,
  },
  nav: {
    flex: 1,
    padding: 'var(--space-2) 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  navItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    position: 'relative',
    transition: 'background-color var(--transition-fast)',
    borderRadius: 0,
    textAlign: 'left',
    minHeight: '44px',
  },
  rowButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    transition: 'background-color var(--transition-fast)',
    minHeight: '44px',
    textAlign: 'left',
  },
  activeIndicator: {
    position: 'absolute',
    left: 0,
    top: '20%',
    bottom: '20%',
    width: '3px',
    backgroundColor: '#ffffff',
    borderRadius: '0 2px 2px 0',
  },
  navLabel: {
    fontSize: 'var(--font-size-sm)',
    lineHeight: 'var(--line-height-tight)',
    whiteSpace: 'nowrap',
    fontFamily: 'var(--font-family-base)',
    fontWeight: '500',
  },
  bottomCluster: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  userBlock: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
    minHeight: '44px',
  },
  initialsCircle: {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    flexShrink: 0,
    cursor: 'default',
  },
  userName: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.85)',
    fontFamily: 'var(--font-family-base)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '120px',
  },
};